import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { MenuItemWithTranslations } from '../types/menu';
import { trackOrder, TrackOrderData } from '../services/orderTrackingService';
import { v4 as uuidv4 } from 'uuid';

export interface CustomerInfo {
  name: string;
  phone: string;
  address: string;
}

export interface OrderData {
  customerInfo: CustomerInfo | null;
  deliveryType: 'surplace' | 'livraison';
  items: Array<{
    name: string;
    quantity: number;
    price: number;
    specialInstructions?: string;
  }>;
  total: number;
  tableInfo: {
    tableNumber: string;
    tableName: string;
  } | null;
  globalSpecialInstructions?: string;
}

export interface CartItem extends MenuItemWithTranslations {
  quantity: number;
  cartId: string;
  specialInstructions?: string;
  hasSpecialInstructions: boolean;
}

/**
 * QR parameters captured at app boot. DeliveryOption navigates to /menu with
 * `replace: true`, which strips the query string before Cart ever mounts, so
 * CartProvider snapshots `?table=&name=&mode=` during its first render.
 */
export interface PendingQrOrder {
  tableNumber: string;
  tableName: string;
  mode: string | null;
}

interface CartContextType {
  isOpen: boolean;
  toggleCart: () => void;
  openCart: () => void;
  items: CartItem[];
  addToCart: (item: MenuItemWithTranslations, quantity?: number) => void;
  removeFromCart: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  updateSpecialInstructions: (id: string, instructions: string) => void;
  total: number;
  itemCount: number;
  deliveryType: 'surplace' | 'livraison' | null;
  setDeliveryType: (type: 'surplace' | 'livraison') => void;
  tableNumber: string | null;
  tableName: string | null;
  setTableInfo: (number: string | null, name: string | null) => void;
  clearCart: () => void;
  submitOrder: (orderData: OrderData) => Promise<void>;
  toggleItemInstructions: (itemId: string) => void;
  pendingQrOrder: PendingQrOrder | null;
  consumePendingQrOrder: () => void;
}

const CartContext = createContext<CartContextType | null>(null);

/**
 * Navigate to a URL and resolve whether the app was sent to the background
 * (i.e. WhatsApp opened) before the timeout. The visibilitychange listener and
 * timer are always cleaned up, so repeated failed attempts cannot leak
 * listeners, and the promise always settles within `timeoutMs`.
 */
const attemptOpenUrl = (url: string, timeoutMs: number): Promise<boolean> =>
  new Promise<boolean>((resolve) => {
    let settled = false;

    const cleanup = () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };

    const finish = (opened: boolean) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(opened);
    };

    const handleVisibilityChange = () => {
      if (document.hidden) finish(true);
    };

    // Register the listener BEFORE assigning location.href so an immediate
    // app hand-off cannot fire the event before anyone is listening.
    document.addEventListener('visibilitychange', handleVisibilityChange);
    const timer = setTimeout(() => finish(false), timeoutMs);

    try {
      window.location.href = url;
    } catch (error) {
      console.error('Failed to navigate to URL:', url, error);
      finish(false);
    }
  });

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [items, setItems] = useState<CartItem[]>([]);
  const [deliveryType, setDeliveryType] = useState<'surplace' | 'livraison' | null>(() => {
    // Get delivery type from localStorage on initial load
    const savedType = localStorage.getItem('deliveryOption');
    return (savedType as 'surplace' | 'livraison' | null) || null;
  });
  const [tableNumber, setTableNumber] = useState<string | null>(null);
  const [tableName, setTableName] = useState<string | null>(null);

  // Captured during the FIRST render, i.e. before DeliveryOption's effect
  // navigates to /menu and strips the query string.
  const [pendingQrOrder, setPendingQrOrder] = useState<PendingQrOrder | null>(() => {
    const params = new URLSearchParams(window.location.search);
    const tableNum = params.get('table');
    const tableNameParam = params.get('name');
    if (tableNum && tableNameParam) {
      return { tableNumber: tableNum, tableName: tableNameParam, mode: params.get('mode') };
    }
    return null;
  });

  const toggleCart = () => setIsOpen((open) => !open);
  const openCart = () => setIsOpen(true);
  const consumePendingQrOrder = () => setPendingQrOrder(null);

  const addToCart = (item: MenuItemWithTranslations, quantity = 1) => {
    // Functional update: rapid +/- taps cannot lose each other's writes.
    setItems((prev) => {
      const existingItem = prev.find((i) => i.id === item.id);
      if (existingItem) {
        return prev.map((i) =>
          i.id === item.id ? { ...i, quantity: i.quantity + quantity } : i
        );
      }
      const cartItem: CartItem = {
        ...item,
        quantity,
        cartId: uuidv4(),
        translations: item.translations,
        specialInstructions: '',
        hasSpecialInstructions: false
      };
      return [...prev, cartItem];
    });
  };

  const removeFromCart = (id: string) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  };

  const updateQuantity = (id: string, quantity: number) => {
    if (quantity < 1) {
      removeFromCart(id);
      return;
    }
    setItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, quantity } : item))
    );
  };

  const updateSpecialInstructions = (id: string, instructions: string) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, specialInstructions: instructions } : item
      )
    );
  };

  const clearCart = () => {
    setItems([]);
    setIsOpen(false);
  };

  const generateWhatsAppMessage = (orderData: OrderData): string => {
    const dateStr = new Date().toLocaleDateString('fr-FR');
    const timeStr = new Date().toLocaleTimeString('fr-FR');
    const deliveryTypeText = orderData.deliveryType === 'surplace' ? '🏠 Sur Place' : '🚚 Livraison';

    const tableInfo = orderData.deliveryType === 'surplace' && orderData.tableInfo
      ? `\n━━━━━━━━━━━━━━━━
🪑 *TABLE ${orderData.tableInfo.tableNumber}*
👤 Nom: ${orderData.tableInfo.tableName}`
      : '';

    const itemsText = orderData.items.map((item, index) =>
      `${index + 1}. *${item.name}*
   • Quantité: ${item.quantity}
   • Prix: ${item.price} DH
   • Total: ${item.price * item.quantity} DH${
     item.specialInstructions
       ? `\n   ℹ️ Note: ${item.specialInstructions}`
       : ''
   }`
    ).join('\n\n');

    return `*NOUVELLE COMMANDE* 🔔
━━━━━━━━━━━━━━━━
📅 ${dateStr}
⏰ ${timeStr}

${deliveryTypeText}${tableInfo}

📋 *DÉTAILS DE LA COMMANDE:*
━━━━━━━━━━━━━━━━
${itemsText}

${orderData.customerInfo
  ? `📍 *INFORMATIONS CLIENT:*
━━━━━━━━━━━━━━━━
👤 Nom: ${orderData.customerInfo.name}
📞 Tél: ${orderData.customerInfo.phone}
🏠 Adresse: ${orderData.customerInfo.address}\n`
  : ''}

💰 *TOTAL À PAYER:* ${orderData.total} DH

✨ Merci de votre commande!`.trim();
  };

  const submitOrder = async (orderData: OrderData): Promise<void> => {
    try {
      // Generate WhatsApp message from the SUBMITTED order (not the outer
      // `total`/live cart), so the customer is always quoted the right price.
      const message = generateWhatsAppMessage(orderData);
      const encodedMessage = encodeURIComponent(message);

      // Create clean order data
      const cleanOrderData: TrackOrderData = {
        id: uuidv4(),
        customerInfo: orderData.customerInfo,
        deliveryType: orderData.deliveryType,
        items: orderData.items.map(item => ({
          name: item.name,
          quantity: item.quantity,
          price: item.price,
          total: item.price * item.quantity
        })),
        total: orderData.total,
        tableInfo: orderData.tableInfo || null,
        globalSpecialInstructions: orderData.globalSpecialInstructions,
        timestamp: Date.now()
      };

      // Tracking is best-effort: a Firebase failure must never stop the
      // customer from placing the order via WhatsApp.
      try {
        await trackOrder(cleanOrderData);
      } catch (error) {
        console.error('Order tracking failed; order still sent via WhatsApp:', error);
      }

      const phoneNumber = '212632302829';
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

      if (isMobile) {
        // Try regular WhatsApp app first, then the web app, then a plain link.
        // Each attempt is bounded by its timeout, so the chain always settles.
        const whatsappOpened = await attemptOpenUrl(
          `whatsapp://send?phone=${phoneNumber}&text=${encodedMessage}`,
          2500
        );

        if (!whatsappOpened) {
          const formattedPhone = phoneNumber.startsWith('212') ? phoneNumber : `212${phoneNumber}`;
          const businessOpened = await attemptOpenUrl(
            `https://api.whatsapp.com/send?phone=${formattedPhone}&text=${encodedMessage}`,
            2500
          );

          if (!businessOpened) {
            // Final fallback: popup, or same-tab navigation if blocked.
            const waMeUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
            const popup = window.open(waMeUrl, '_blank');
            if (!popup) {
              window.location.href = waMeUrl;
            }
          }
        }
      } else {
        // On desktop, open WhatsApp Web directly (with a same-tab fallback
        // if the popup is blocked).
        const webUrl = `https://web.whatsapp.com/send?phone=${phoneNumber}&text=${encodedMessage}`;
        const popup = window.open(webUrl, '_blank');
        if (!popup) {
          window.location.href = webUrl;
        }
      }

      clearCart();
      setIsOpen(false);

    } catch (error) {
      console.error('Error submitting order:', error);
      throw error;
    }
  };

  const total = useMemo(
    () => items.reduce((sum, item) => sum + (item.price * item.quantity), 0),
    [items]
  );
  const itemCount = useMemo(
    () => items.reduce((sum, item) => sum + item.quantity, 0),
    [items]
  );

  const setTableInfo = (number: string | null, name: string | null) => {
    if (number && name) {
      setTableNumber(number);
      setTableName(name);
      setDeliveryType('surplace');
      // Store in localStorage
      localStorage.setItem('tableNumber', number);
      localStorage.setItem('tableName', name);
      localStorage.setItem('deliveryOption', 'surplace');
    }
  };

  const toggleItemInstructions = (itemId: string) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === itemId
          ? { ...item, hasSpecialInstructions: !item.hasSpecialInstructions }
          : item
      )
    );
  };

  // Update setDeliveryType to persist the selection
  const handleDeliveryTypeChange = (type: 'surplace' | 'livraison') => {
    setDeliveryType(type);
    localStorage.setItem('deliveryOption', type);
  };

  // Initialize from localStorage and keep deliveryType / deliveryOption in sync:
  // a saved table means "surplace" (and the stored option must agree), otherwise
  // restore whatever option the user picked earlier.
  useEffect(() => {
    const savedTableNumber = localStorage.getItem('tableNumber');
    const savedTableName = localStorage.getItem('tableName');
    if (savedTableNumber && savedTableName) {
      setTableNumber(savedTableNumber);
      setTableName(savedTableName);
      setDeliveryType('surplace');
      if (localStorage.getItem('deliveryOption') !== 'surplace') {
        localStorage.setItem('deliveryOption', 'surplace');
      }
    } else {
      const savedOption = localStorage.getItem('deliveryOption');
      if (savedOption === 'surplace' || savedOption === 'livraison') {
        setDeliveryType(savedOption);
      }
    }
  }, []);

  return (
    <CartContext.Provider value={{
      isOpen,
      toggleCart,
      openCart,
      items,
      addToCart,
      removeFromCart,
      updateQuantity,
      updateSpecialInstructions,
      total,
      itemCount,
      deliveryType,
      setDeliveryType: handleDeliveryTypeChange,
      tableNumber,
      tableName,
      setTableInfo,
      clearCart,
      submitOrder,
      toggleItemInstructions,
      pendingQrOrder,
      consumePendingQrOrder,
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
